/* Imports */

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseEndpoint, doorsPath, doorSensorsPath } from "../../api/endpoints";
import { getToken } from "../../api/getToken";
import {
  floorSvg,
  floorType,
  groupType,
  deskType,
  sensorType,
} from "../../interfaces/groups";
import { RolesWithPermissons } from "../../interfaces/roles";

/* Slice */

const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: fetchBaseQuery({
    baseUrl: baseEndpoint,
    prepareHeaders: async (headers) => {
      // Get the token from local storage
      const token = await getToken();

      // If a token is found, add it to the headers
      if (token) {
        headers.set("Authorization", `${token}`);
      }

      return headers;
    },
  }),
  tagTypes: [
    "Locations",
    "Floors",
    "FavFloors",
    "Location",
    "Desks",
    "Desk",
    "Doors",
    "Door",
    "DoorSensors",
    "Sensors",
    "Sensor",
    "Floor",
    "Roles",
    "s3Url",
    "Reports",
    "Users",
    "User",
    "Stats",
    "EmailAlerts",
  ],

  endpoints: (builder) => ({
    getLocations: builder.query({
      query: () => `/locations`,
      transformResponse: (response: {
        payload: groupType[];
        success: boolean;
      }) => {
        return response.payload;
      },
      providesTags: ["Locations"],
    }),
    getLocation: builder.query({
      query: (id) => `/locations/${id}`,
      transformResponse: (response: {
        payload: groupType[];
        success: boolean;
      }) => {
        return response.payload;
      },
    }),
    getFloors: builder.query({
      query: (id) => `/floors?locationID=${id}`,
      transformResponse: (response: {
        payload: floorType[];
        success: boolean;
      }) => {
        return response.payload;
      },
      providesTags: ["Floors"],
    }),
    getAllFloors: builder.query({
      query: () => `/floors`,
      transformResponse: (response: {
        payload: floorType[];
        success: boolean;
      }) => {
        return response.payload;
      },
    }),

    getFavFloors: builder.query({
      async queryFn(locationIDs, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          const floorsPromises = locationIDs.map(async (locationId) => {
            const result = await fetchWithBQ(
              `/floors?locationID=${locationId}`
            );

            if (result.error) {
              throw result.error;
            }
            //@ts-ignore
            return result.data.payload;
          });

          const allFloors = await Promise.all(floorsPromises);

          const flattenedFloors = allFloors.flat();

          return { data: flattenedFloors };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["FavFloors"],
    }),
    getRoles: builder.query({
      query: () => "/roles",
      transformResponse: (response: {
        payload: RolesWithPermissons;
        success: boolean;
      }) => {
        return response.payload;
      },
      providesTags: ["Roles"],
    }),

    getFloor: builder.query<floorSvg, string>({
      query: (id) => `/floors/${id}`,
      transformResponse: (response: {
        payload: floorSvg;
        success: boolean;
      }) => {
        return response.payload;
      },
      providesTags: ["Floor"],
    }),
    getDesks: builder.query({
      query: (floorID) => `/desks?floorID=${floorID}`,
      transformResponse: (response: {
        payload: deskType[];
        success: boolean;
      }) => {
        return response.payload;
      },
      providesTags: ["Desks"],
    }),
    getDesk: builder.query({
      query: (deskID) => `/desks/${deskID}`,
      transformResponse: (response: {
        payload: deskType;
        success: boolean;
      }) => {
        return response.payload;
      },
      providesTags: ["Desk"],
    }),
    getSensors: builder.query({
      query: ({ id, assigned }) =>
        `/sensors?floorID=${id}&assigned=${assigned}&isFiltered=false`,
      transformResponse: (response: {
        payload: sensorType[];
        success: boolean;
      }) => {
        return response.payload;
      },
      providesTags: ["Sensors"],
    }),
    getSensor: builder.query({
      query: (sensorID) => `/sensors/${sensorID}`,
      transformResponse: (response: {
        payload: sensorType;
        success: boolean;
      }) => {
        return response.payload;
      },
      providesTags: ["Sensor"],
    }),
    editFloorPositions: builder.mutation({
      query: ({ locationID, content }) => ({
        url: `/floors?locationID=${locationID}`,
        method: "PATCH",
        body: content,
      }),
      transformResponse: (response: { payload: any; success: boolean }) => {
        return response.success;
      },
      invalidatesTags: ["Floors"],
    }),

    getS3Url: builder.query({
      query: ({ fileName }) => `/upload?fileName=${fileName}`,
      transformResponse: (response: { payload: any; success: boolean }) => {
        return response.payload;
      },
      providesTags: ["s3Url"],
    }),
    updateDeskInfo: builder.mutation({
      query: ({ deskID, updatedData, action }) => ({
        url: `/desks/${deskID}?action=${action}`,
        method: "PATCH",
        body: updatedData,
      }),
      transformResponse: (response: { payload: []; success: boolean }) => {
        return response.success;
      },
      invalidatesTags: ["Desks", "Desks", "Sensors", "Sensor"],
    }),

    // TODO: PATCH /doors is not deployed yet, confirm rename/status contract with BE
    updateDoorInfo: builder.mutation({
      query: ({ doorID, updatedData, action }) => ({
        url: `/doors/${doorID}?action=${action}`,
        method: "PATCH",
        body: updatedData,
      }),
      transformResponse: (response: { payload: []; success: boolean }) => {
        return response.success;
      },
      invalidatesTags: ["Doors", "DoorSensors"],
    }),
    // assigned=false returns the global unassigned pool
    getDoorSensors: builder.query({
      query: ({ floorID, assigned }) =>
        assigned
          ? `${doorSensorsPath}?assigned=true&floorID=${floorID}`
          : `${doorSensorsPath}?assigned=false`,
      transformResponse: (response: {
        payload: sensorType[];
        success: boolean;
      }) => {
        return response.payload;
      },
      providesTags: ["DoorSensors"],
    }),
    // pool ids are "door-sensor#<EUI>" but the route takes the bare EUI
    assignDoorSensor: builder.mutation({
      query: ({ sensorID, floorID, alias }) => ({
        url: `${doorSensorsPath}/${encodeURIComponent(
          sensorID.replace(/^door-sensor#/, "")
        )}?action=assign`,
        method: "PATCH",
        body: { floorID, alias },
      }),
      transformResponse: (response: { payload: []; success: boolean }) => {
        return response.success;
      },
      invalidatesTags: ["Doors", "DoorSensors"],
    }),
    unassignDoorSensor: builder.mutation({
      query: ({ sensorID }) => ({
        url: `${doorSensorsPath}/${encodeURIComponent(
          sensorID.replace(/^door-sensor#/, "")
        )}?action=unassign`,
        method: "PATCH",
      }),
      transformResponse: (response: { payload: []; success: boolean }) => {
        return response.success;
      },
      invalidatesTags: ["Doors", "DoorSensors"],
    }),
    // period: day | week | month, granularity: hour | day,
    // timeStart/timeEnd: optional YYYY-MM-DD or RFC3339 custom range
    getDoorGraphs: builder.query({
      query: ({ floorID, period, granularity, timeStart, timeEnd }) => {
        const params = new URLSearchParams({ floorID });
        if (period) params.append("period", period);
        if (granularity) params.append("granularity", granularity);
        if (timeStart) params.append("timeStart", timeStart);
        if (timeEnd) params.append("timeEnd", timeEnd);
        return `${doorsPath}/graphs?${params.toString()}`;
      },
      transformResponse: (response: { payload: unknown; success: boolean }) => {
        return response?.payload ?? response;
      },
      providesTags: ["Doors"],
    }),

    editFloorData: builder.mutation({
      query: ({ floorID, floorData, requestType }) => ({
        url: `/floors/${floorID}?requestType=${requestType}`,
        method: "PATCH",
        body: floorData,
      }),
      transformResponse: (response: { payload: []; success: boolean }) => {
        return response.success;
      },
      invalidatesTags: ["Floors", "Location"],
    }),

    deleteFloor: builder.mutation({
      query: ({ floorID }) => ({
        url: `/floors/${floorID}`,
        method: "DELETE",
      }),
      transformResponse: (response: { payload: []; success: boolean }) => {
        return response.success;
      },
      invalidatesTags: ["Floors"],
    }),
    deletelocation: builder.mutation({
      query: ({ locationID }) => ({
        url: `/locations/${locationID}`,
        method: "DELETE",
      }),
      transformResponse: (response: { payload: []; success: boolean }) => {
        return response.success;
      },
      invalidatesTags: ["Locations"],
    }),
    addFloors: builder.mutation({
      query: ({ id, floorData }) => ({
        url: `/floors?locationID=${id}`,
        method: "POST",
        body: floorData,
      }),
      transformResponse: (response: { payload: any; success: boolean }) => {
        return { success: response.success, id: response.payload.id };
      },
      invalidatesTags: ["Floors"],
    }),
    syncSensorFloor: builder.query({
      query: ({floorID}) => `/sensors/sync/${floorID}`,
      transformResponse: (response: { payload: any; success: boolean }) => {
        return response.success;
      },
    }),
    updateLocation: builder.mutation({
      query: ({ id, locationData, action }) => ({
        url: `/locations/${id}?action=${action}`,
        method: "PATCH",
        body: locationData,
      }),
      transformResponse: (response: { payload: []; success: boolean }) => {
        return response.success;
      },
      invalidatesTags: ["Locations"],
    }),
    toggleLocationLiveStatus: builder.mutation({
      query: ({ id, locationData }) => ({
        url: `/locations/${id}?action=live`,
        method: "PATCH",
        body: locationData,
      }),
      transformResponse: (response: { payload: []; success: boolean }) => {
        return response.success;
      },
    }),
    addLocations: builder.mutation({
      query: ({ location }) => ({
        url: `/locations`,
        method: "POST",
        body: location,
      }),
      transformResponse: (response: { payload: []; success: boolean }) => {
        return response.success;
      },
      invalidatesTags: ["Locations"],
    }),

    getLogs: builder.query({
      query: ({ timeStart, timeEnd, PaginationToken, limit, filters }) => ({
        url: `/logs?timeStart=${timeStart}&timeEnd=${timeEnd}`,
        method: "POST",

        body: {
          limit: parseInt(limit),
          ...(PaginationToken && { paginationToken: PaginationToken }),
          ...(Object.keys(filters).length > 0 && { filters }),
        },
      }),
      transformResponse: (response: { payload: any; success: boolean }) => {
        return response.payload;
      },

      // providesTags: ["Locations"],
    }),
    getAlertEmail: builder.query({
      query: ({ paginationToken, searchQuery }) => ({
        url: paginationToken
          ? `/alert-email/search?paginationToken=${paginationToken}&searchQuery=${searchQuery}`
          : `/alert-email/search?searchQuery=${searchQuery}`,
        method: "GET",
      }),
      transformResponse: (response: { payload: any; success: boolean }) => {
        return response.payload;
      },

      providesTags: ["EmailAlerts"],
    }),
    deleteAlertEmail: builder.mutation({
      query: ({ id }) => ({
        url: `/alert-email/${id}`,
        method: "DELETE",
      }),

      transformResponse: (response: { payload: any; success: boolean }) => {
        return response.success;
      },

      invalidatesTags: ["EmailAlerts"],
    }),
    syncFlagStatus: builder.query({
      query: () => ({
        url: "/sensors/sync-status",
        method: "GET",
      }),
      transformResponse: (response: {
        payload: "SUCCEEDED" | "FAILED" | "RUNNING";
        success: boolean;
      }) => {
        return response.payload;
      },
    }),
    getActivityLogs: builder.query({
      query: ({ timeEnd, timeStart, paginationToken, limit, eui }) => {
        const token = JSON.stringify(paginationToken);

        return {
          url: paginationToken
            ? `/sensors/activity-log?timeEnd=${timeEnd}&timeStart=${timeStart}&limit=${limit}&paginationToken=${encodeURIComponent(
                token
              )}&eui=${eui}`
            : `/sensors/activity-log?timeEnd=${timeEnd}&timeStart=${timeStart}&limit=${limit}&eui=${eui}`,
          method: "GET",
        };
      },
      transformResponse: (response: { payload: any; success: boolean }) => {
        return response.payload;
      },
    }),
    editAlertEmail: builder.mutation({
      query: ({ id, content }) => ({
        url: `/alert-email?${id}`,
        method: "PATCH",
        body: content,
      }),

      transformResponse: (response: { payload: any; success: boolean }) => {
        return response.success;
      },

      invalidatesTags: ["EmailAlerts"],
    }),
    addAlertEmail: builder.mutation({
      query: (content) => ({
        url: `/alert-email`,
        method: "POST",
        body: { email: content },
      }),

      transformResponse: (response: { payload: any; success: boolean }) => {
        return response.success;
      },

      invalidatesTags: ["EmailAlerts"],
    }),
    issueReport: builder.mutation({
      query: (content) => ({
        url: `/hcc-report`,
        method: "POST",
        body: content,
      }),
      transformResponse: (response: { payload: []; success: boolean }) => {
        return response.success;
      },
    }),
    searchUsers: builder.query({
      query: (searchTerm) => ({
        url: `/users/search?search=${searchTerm}`,
        method: "GET",
      }),
      transformResponse: (response: { payload: any; success: boolean }) => {
        return response.payload;
      },
      providesTags: ["Users"], 
    }),
    getUsers: builder.query({
      query: (paginationToken) => ({
        url: paginationToken
          ? `/users?paginationToken=${paginationToken}`
          : `/users`,
        method: "GET",
      }),
      transformResponse: (response: { payload: any; success: boolean }) => {
        return response.payload;
      },
      providesTags: ["Users"],
    }),
    getUser: builder.query({
      query: (id) => ({
        url: `/users/${id}`,
        method: "GET",
      }),
      transformResponse: (response: { payload: any; success: boolean }) => {
        return response.payload;
      },
      providesTags: ["User"],
    }),
    deleteUser: builder.mutation({
      query: (id) => ({
        url: `/users/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response: { payload: any; success: boolean }) => {
        return response.success;
      },
      invalidatesTags: ["Users", "User"],
    }),

    getReports: builder.query({
      query: (content) => ({
        url: `/reports`,
        method: "POST",
        body: JSON.stringify(content),
      }),
      transformResponse: (response: { payload: any; success: boolean }) => {
        return response.payload;
      },
    }),
    getReportsV1: builder.query({
      query: ({ content, graph, params = {} }) => {
        const queryParams = new URLSearchParams();

        if (graph) {
          queryParams.append("graph", graph);
        }

        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            queryParams.append(key, String(value));
          }
        });

        const queryString = queryParams.toString();
        return {
          url: queryString ? `/reports/v1?${queryString}` : `/reports/v1`,
          method: "POST",
          body: JSON.stringify(content),
        };
      },
      transformResponse: (response: { payload: any; success: boolean }) => {
        return response.payload;
      },
    }),
    exportReports: builder.query({
      query: (content) => ({
        url: `/reports/export`,
        method: "POST",
        body: JSON.stringify(content),
      }),
      transformResponse: (response: { payload: any; success: boolean }) => {
        return response.success;
      },
    }),

    exportLogs: builder.query({
      query: ({ resource, timeStart, timeEnd }) =>
        resource === "all"
          ? `/logs/export?timeStart=${timeStart}&timeEnd=${timeEnd}`
          : `/logs/export?resource=${resource}&timeStart=${timeStart}&timeEnd=${timeEnd}`,
      transformResponse: (response: { payload: any; success: boolean }) => {
        return response.payload;
      },
    }),
    getStats: builder.query({
      query: (locationID) => `/statistics/${locationID}`,
      transformResponse: (response: { payload: []; success: boolean }) => {
        return response.payload;
      },
      providesTags: ["Stats"],
    }),
    createUser: builder.mutation({
      query: (content) => ({
        url: `/users`,
        method: "POST",
        body: content,
      }),
      transformResponse: (response: { payload: any; success: boolean }) => {
        return response.success;
      },
      invalidatesTags: ["Users"],
    }),
    EditUser: builder.mutation({
      query: ({ content, id }) => ({
        url: `/users/${id}`,
        method: "PATCH",
        body: content,
      }),
      transformResponse: (response: { payload: []; success: boolean }) => {
        return response.success;
      },
      invalidatesTags: ["Users", "User"],
    }),
  }),
});

export const {
  useGetLocationsQuery,
  useGetLocationQuery,
  useLazySyncSensorFloorQuery,
  useLazyGetLocationQuery,
  useGetFloorsQuery,
  useLazyGetFloorsQuery,
  useGetLogsQuery,
  useLazyGetLocationsQuery,
  useLazyGetLogsQuery,
  useGetDeskQuery,
  useLazyGetDeskQuery,
  useLazyGetDesksQuery,
  useGetFloorQuery,
  useLazyGetFloorQuery,
  useAddFloorsMutation,
  useGetSensorQuery,
  useLazyGetSensorQuery,
  useUpdateLocationMutation,
  useGetSensorsQuery,
  useGetDesksQuery,
  useUpdateDoorInfoMutation,
  useGetDoorSensorsQuery,
  useLazyGetDoorSensorsQuery,
  useAssignDoorSensorMutation,
  useUnassignDoorSensorMutation,
  useGetDoorGraphsQuery,
  useLazyGetDoorGraphsQuery,
  useAddLocationsMutation,
  useDeletelocationMutation,
  useDeleteUserMutation,
  useDeleteFloorMutation,
  useUpdateDeskInfoMutation,
  useGetFavFloorsQuery,
  useGetRolesQuery,
  useToggleLocationLiveStatusMutation,
  useLazyGetS3UrlQuery,
  useEditFloorPositionsMutation,
  useEditFloorDataMutation,
  useGetReportsQuery,
  useLazyGetReportsQuery,
  useGetReportsV1Query,
  useLazyGetReportsV1Query,
  useGetAllFloorsQuery,
  useIssueReportMutation,
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useLazyExportLogsQuery,
  useGetStatsQuery,
  useLazyGetStatsQuery,
  useLazyGetUserQuery,
  useCreateUserMutation,
  useEditUserMutation,
  useExportReportsQuery,
  useLazyExportReportsQuery,
  useGetAlertEmailQuery,
  useEditAlertEmailMutation,
  useDeleteAlertEmailMutation,
  useAddAlertEmailMutation,
  useLazyGetAlertEmailQuery,
  useLazySyncFlagStatusQuery,
  useSyncFlagStatusQuery,
  useLazyGetActivityLogsQuery,
  useLazySearchUsersQuery
} = apiSlice;

export default apiSlice;
